/* © CNES */
import { useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../../../context/ConfigContext';
import { FilterDataContext } from '../../../context/FilterDataContext';
import { parseFilterContextToUrl } from '../Utils';

/**
 * Custom hook to automatically update the browser's URL based on changes
 * in the application's filter data, map extent, or drawn shapes.
 *
 * Dependencies:
 * - ConfigContext: Provides `drawnShape` and `mapExtent`.
 * - FilterDataContext: Provides `filterData` for constructing the query string.
 * - React Router's `useNavigate` for programmatic navigation.
 */
const useUrlUpdater = () => {
  const navigate = useNavigate();
  const { drawnShape, mapExtent, searchZone } = useContext(ConfigContext);
  const { filterData } = useContext(FilterDataContext);

  useEffect(() => {
    const urlParams = parseFilterContextToUrl(
      { ...filterData },
      drawnShape,
      mapExtent.currentView ? mapExtent.extent : undefined,
      searchZone
    );

    navigate({
      pathname: urlParams ? '/search' : '/',
      ...(urlParams && { search: urlParams }),
    });
  }, [mapExtent, filterData, drawnShape, searchZone, navigate]);
};

export default useUrlUpdater;
